import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { BlCheckboxInput as Checkbox } from "Components/_Shared/Inputs/BlCheckboxInput";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { getListInsuranceContractsQueryKey, useUpdateInsuranceContract } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Contract } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

const StyledWrapper = styled(Stack, {
  shouldForwardProp: isPropValid,
})<{ $selected?: boolean }>`
  ${({ theme, $selected }) => `
    cursor: pointer;
    width: 100%;
    border: ${$selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.colors.borderLight}`};
    border-radius: ${theme.spacing(Radius.L)};
    padding: ${theme.spacing(Spacing.L)};
  `}
`;

type Props = {
  readonly clientId: string;
  readonly insuranceContract: Contract;
  readonly partnerName: string;
  readonly selected?: boolean;
  readonly onEdit?: () => void;
};

export const ClientSolutionTile: React.FC<Props> = ({ clientId, insuranceContract, selected, partnerName, onEdit }) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();

  const { mutateAsync: updateInsuranceContract } = useUpdateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractEdited));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  return (
    <StyledWrapper
      spacing={Spacing.M}
      $selected={selected}
      boxShadow={1}
      onClick={() => {
        onEdit?.();
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {partnerName}
          </Typography>
          <Checkbox
            value={selected}
            checked={selected}
            onClick={async (event) => {
              try {
                event.stopPropagation();

                const { guid, ...contractBody } = insuranceContract;
                await updateInsuranceContract({
                  analysisId,
                  versionId,
                  clientGuid: clientId,
                  contractGuid: guid,
                  data: {
                    ...contractBody,
                    createRecommended: !selected,
                  },
                });
              } catch {
                toast.error(t(Resources.Common.Error));
              }
            }}
          />
        </Box>
        <Typography variant="body2" color="secondary">
          {insuranceContract.product}
        </Typography>
      </Box>
      <TextInput
        value={insuranceContract.insuranceAmount.value.toString()}
        disabled={readOnly}
        name="compulsoryInsuranceCurrentAmount"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onBlur={async (event) => {
          try {
            const { guid, ...contractBody } = insuranceContract;
            await updateInsuranceContract({
              analysisId,
              versionId,
              clientGuid: clientId,
              contractGuid: guid,
              data: {
                ...contractBody,
                insuranceAmount: {
                  ...insuranceContract.insuranceAmount,
                  value: Number(event.target.value.replaceAll(" ", "")),
                },
              },
            });
          } catch {
            toast.error(t(Resources.Common.Error));
          }
        }}
        mask={[
          {
            mask: Number,
            thousandsSeparator: " ",
            scale: 0,
            min: 0,
          },
        ]}
        inputEndAdornment={
          <Typography minWidth="fit-content" variant="body2" color="secondary" width={92} textAlign="right">
            {t(Resources.Common.CZKSuffix)}/{getAmountFrequencyTranslation(insuranceContract.insuranceAmount.frequency, t)}
          </Typography>
        }
      />
    </StyledWrapper>
  );
};
